import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';

import { RightPanelTab } from '../../pages/PlanningPageHelpers';
import { errorMessage } from '../../services/apiError';
import { ChatMessage } from '../../services/chatService';
import {
  projectService,
  type PendingTitleConfirmation,
  type Project,
  VcsStatus,
} from '../../services/projectService';

interface PlanningState {
  // Active project/session request identity
  activeProjectId: string | null;
  activeSessionId: string | null;
  currentFetchRequestId: string | null;

  // Chat State
  messages: ChatMessage[];
  inputValue: string;
  /** Busy state for sending/streaming a message. Project bootstrap is tracked separately. */
  isLoading: boolean;
  error: string | null;

  // Project bootstrap state is separate from message sending so either
  // operation cannot prematurely finish or restart the other one's UI.
  projectLoadStatus: ProjectLoadStatus;
  projectLoadError: string | null;
  hasProjectSnapshot: boolean;

  // Streaming State
  isAiTyping: boolean;
  streamingDisplay: string;
  currentStreamingMsgId: string | null;

  // Project State
  projectTitle: string | null;
  projectRole: string | null;
  projectIsPublic: boolean | null;
  titleConfirmedAt: string | null;
  titleChangedByUserAt: string | null;
  pendingTitleConfirmation: PendingTitleConfirmation | null;
  codeReady: boolean;
  vcsStatus: VcsStatus;
  hasAgentRepository: boolean;

  // Panel State
  activeRightTab: RightPanelTab;
}

type FetchProjectArg = string | {
  projectId: string;
  sessionId?: string | null;
};

type ProjectSelection = {
  projectId?: string | null;
  sessionId?: string | null;
};

export type ProjectLoadStatus = 'idle' | 'pending' | 'refreshing' | 'succeeded' | 'failed';

const getFetchProjectSelection = (arg: FetchProjectArg): Required<ProjectSelection> => ({
  projectId: typeof arg === 'string' ? arg : arg.projectId,
  sessionId: typeof arg === 'string' ? null : arg.sessionId ?? null,
});

const matchesActiveFetch = (
  state: PlanningState,
  requestId: string,
  arg: FetchProjectArg,
): boolean => {
  const selection = getFetchProjectSelection(arg);

  return state.currentFetchRequestId === requestId
    && state.activeProjectId === selection.projectId
    && state.activeSessionId === selection.sessionId;
};

/**
 * Merge a fetched message snapshot with the current list instead of replacing
 * it: an error-triggered refetch (useProjectReconnect) races live SSE/stream
 * appends, and the server snapshot may predate messages already added locally.
 * Keeps local-only messages appended after the snapshot; temp-* optimistic
 * messages are excluded because the sender's finalize/error paths own them.
 * Project/session switches reset messages first, so nothing leaks across.
 */
const mergeFetchedMessages = (
  existing: ChatMessage[],
  fetched: ChatMessage[],
): ChatMessage[] => {
  const fetchedIds = new Set(fetched.map((m) => m.id));
  // Only keep local-only messages NEWER than the snapshot's newest row: those
  // are live appends the fetch raced past. Older local-only messages were
  // deleted server-side — re-appending them would resurrect them.
  const newestFetchedTs = fetched.reduce((max, m) => (m.created_at > max ? m.created_at : max), '');
  const localOnly = existing.filter((m) => (
    !fetchedIds.has(m.id)
    && !m.id.startsWith('temp-')
    && m.created_at >= newestFetchedTs
  ));

  return localOnly.length ? [...fetched, ...localOnly] : fetched;
};

const initialState: PlanningState = {
  activeProjectId: null,
  activeSessionId: null,
  currentFetchRequestId: null,
  messages: [],
  inputValue: '',
  isLoading: false,
  error: null,
  projectLoadStatus: 'idle',
  projectLoadError: null,
  hasProjectSnapshot: false,
  isAiTyping: false,
  streamingDisplay: '',
  currentStreamingMsgId: null,
  projectTitle: null,
  projectRole: null,
  projectIsPublic: null,
  titleConfirmedAt: null,
  titleChangedByUserAt: null,
  pendingTitleConfirmation: null,
  codeReady: false,
  vcsStatus: 'pending',
  hasAgentRepository: false,
  activeRightTab: 'ai-overview',
};

// eslint-disable-next-line max-statements
const resetForSelection = (state: PlanningState, selection?: ProjectSelection) => {
  const savedTab = state.activeRightTab;
  const nextProjectId = selection?.projectId ?? null;
  const nextSessionId = selection?.sessionId ?? null;

  // StrictMode, Fast Refresh, and same-route remounts may repeat the
  // bootstrap effect. Keep the settled snapshot on screen while the
  // follow-up fetch revalidates it instead of flashing an empty page.
  if (
    state.activeProjectId === nextProjectId
    && state.activeSessionId === nextSessionId
  ) return;

  const sameProject = Boolean(nextProjectId) && state.activeProjectId === nextProjectId;
  const retainedProject = sameProject ? {
    projectTitle: state.projectTitle,
    projectRole: state.projectRole,
    projectIsPublic: state.projectIsPublic,
    titleConfirmedAt: state.titleConfirmedAt,
    titleChangedByUserAt: state.titleChangedByUserAt,
    codeReady: state.codeReady,
    vcsStatus: state.vcsStatus,
    hasAgentRepository: state.hasAgentRepository,
  } : null;

  Object.assign(state, initialState);
  state.activeProjectId = nextProjectId;
  state.activeSessionId = nextSessionId;
  state.activeRightTab = savedTab;
  if (retainedProject) Object.assign(state, retainedProject);
};

type ProjectDetail = Awaited<ReturnType<typeof projectService.getProject>>;

// eslint-disable-next-line max-statements
const applyFetchedProject = (state: PlanningState, payload: ProjectDetail) => {
  state.messages = mergeFetchedMessages(state.messages, payload.messages);
  state.projectTitle = payload.project.title;
  state.projectRole = payload.project.role ?? null;
  state.projectIsPublic = payload.project.is_public ?? null;
  state.titleConfirmedAt = payload.project.title_confirmed_at ?? null;
  state.titleChangedByUserAt = payload.project.title_changed_by_user_at ?? null;
  state.pendingTitleConfirmation = payload.pending_title_confirmation ?? null;
  state.codeReady = payload.project.code_ready ?? false;
  state.vcsStatus = payload.project.vcs_status ?? 'pending';
  state.hasAgentRepository = payload.project.has_agent_repository ?? false;
  state.currentFetchRequestId = null;
  state.projectLoadStatus = 'succeeded';
  state.projectLoadError = null;
  state.hasProjectSnapshot = true;
};

// Async Thunks
export const fetchProject = createAsyncThunk(
  'planning/fetchProject',
  async (arg: FetchProjectArg, { rejectWithValue }) => {
    try {
      const projectId = typeof arg === 'string' ? arg : arg.projectId;
      const sessionId = typeof arg === 'string' ? null : arg.sessionId ?? null;
      const data = await projectService.getProject(projectId, { sessionId });

      return data;
    } catch (error: unknown) {
      return rejectWithValue(errorMessage(error, 'We couldn’t load this project. Refresh and try again.'));
    }
  }
);

const planningSlice = createSlice({
  name: 'planning',
  initialState,
  reducers: {
    // Chat Actions
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
    },

    setInputValue: (state, action: PayloadAction<string>) => {
      state.inputValue = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Streaming Actions
    setTypingState: (state, action: PayloadAction<{ isAiTyping: boolean }>) => {
      state.isAiTyping = action.payload.isAiTyping;
    },
    setStreamingDisplay: (state, action: PayloadAction<string>) => {
      state.streamingDisplay = action.payload;
    },
    appendStreamingDisplay: (state, action: PayloadAction<string>) => {
      state.streamingDisplay += action.payload;
    },
    setCurrentStreamingMsgId: (state, action: PayloadAction<string | null>) => {
      state.currentStreamingMsgId = action.payload;
    },

    // Project Actions
    setProjectTitle: (state, action: PayloadAction<string | null>) => {
      state.projectTitle = action.payload;
    },
    setProjectTitleConfirmation: (
      state,
      action: PayloadAction<Pick<Project, 'title' | 'title_confirmed_at' | 'title_changed_by_user_at'>>,
    ) => {
      state.projectTitle = action.payload.title;
      state.titleConfirmedAt = action.payload.title_confirmed_at ?? null;
      state.titleChangedByUserAt = action.payload.title_changed_by_user_at ?? null;
      state.pendingTitleConfirmation = null;
    },
    setCodeReady: (state, action: PayloadAction<boolean>) => {
      state.codeReady = action.payload;
    },
    setVcsStatus: (state, action: PayloadAction<VcsStatus>) => {
      state.vcsStatus = action.payload;
    },
    setHasAgentRepository: (state, action: PayloadAction<boolean>) => {
      state.hasAgentRepository = action.payload;
    },

    // Panel Actions
    setActiveRightTab: (state, action: PayloadAction<RightPanelTab>) => {
      state.activeRightTab = action.payload;
    },
    // Reset all project-specific state when switching projects
    resetProjectState: (state, action: PayloadAction<ProjectSelection | undefined>) => (
      resetForSelection(state, action.payload)
    ),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProject.pending, (state, action) => {
      const selection = getFetchProjectSelection(action.meta.arg);
      const isRevalidating = (
        state.activeProjectId === selection.projectId
        && state.activeSessionId === selection.sessionId
        && state.hasProjectSnapshot
      );

      state.activeProjectId = selection.projectId;
      state.activeSessionId = selection.sessionId;
      state.currentFetchRequestId = action.meta.requestId;
      state.projectLoadStatus = isRevalidating ? 'refreshing' : 'pending';
      state.projectLoadError = null;
    });
    builder.addCase(fetchProject.fulfilled, (state, action) => {
      if (!matchesActiveFetch(state, action.meta.requestId, action.meta.arg)) return;
      applyFetchedProject(state, action.payload);
    });
    builder.addCase(fetchProject.rejected, (state, action) => {
      if (!matchesActiveFetch(state, action.meta.requestId, action.meta.arg)) return;

      state.projectLoadError = action.payload as string;
      state.currentFetchRequestId = null;
      state.projectLoadStatus = 'failed';
    });
  },
});

export const {
  setMessages,
  setInputValue,
  setIsLoading,
  setError,
  setTypingState,
  setStreamingDisplay,
  appendStreamingDisplay,
  setCurrentStreamingMsgId,
  setProjectTitle,
  setProjectTitleConfirmation,
  setCodeReady,
  setHasAgentRepository,
  setVcsStatus,
  setActiveRightTab,
  resetProjectState,
} = planningSlice.actions;

export default planningSlice.reducer;
